import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import {
  forceRefreshSession,
  markOnboardingSubmitted,
  signOut,
  userKeyFromClaims,
  getIdTokenClaims,
} from "../../common/auth";
import { ADMIN_CONTACT_EMAIL } from "../../common/onboarding";
import { getTenant, submitTenantForApproval } from "../../common/tenantApi";
import {
  isTenantApprovedForApp,
  syncOnboardingFlagsFromTenant,
} from "../../common/tenantAccess";
import { trackEvent } from "../../hooks/useTrackEvent";
import ProductPreviewShowcase, { SLIDES } from "./ProductPreviewShowcase";
import "./PendingApproval.css";

function buildMailto(tenant) {
  const tenantName = tenant?.name || sessionStorage.getItem("onboarding_tenant_name") || "mi workspace";
  const tenantId = tenant?.tenant_id || sessionStorage.getItem("onboarding_tenant_id") || "";
  const contactEmail = tenant?.contact?.email || "";
  const phone = tenant?.contact?.phone || "";
  const subject = encodeURIComponent(`Solicitud Tagnize — ${tenantName} (${tenantId})`);
  const body = encodeURIComponent(
    `Hola,\n\nMi solicitud de alta está pendiente.\n\n` +
      `Workspace: ${tenantName}\n` +
      `Tenant ID: ${tenantId}\n` +
      `Email contacto: ${contactEmail}\n` +
      `Teléfono: ${phone}\n`
  );
  return `mailto:${ADMIN_CONTACT_EMAIL}?subject=${subject}&body=${body}`;
}

export default function PendingApprovalPage() {
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [slide, setSlide] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadTenant = useCallback(async () => {
    try {
      const data = await getTenant();
      setTenant(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await loadTenant();
      if (cancelled) return;
      const claims = await getIdTokenClaims().catch(() => ({}));
      const userKey = userKeyFromClaims(claims);
      if (data && isTenantApprovedForApp(data)) {
        syncOnboardingFlagsFromTenant(data, userKey);
        await forceRefreshSession().catch(() => {});
        navigate("/Home", { replace: true });
        return;
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadTenant, navigate]);

  useEffect(() => {
    const id = setInterval(async () => {
      const data = await loadTenant();
      if (!data || !isTenantApprovedForApp(data)) return;
      const claims = await getIdTokenClaims().catch(() => ({}));
      const userKey = userKeyFromClaims(claims);
      syncOnboardingFlagsFromTenant(data, userKey);
      await forceRefreshSession().catch(() => {});
      navigate("/Home", { replace: true });
    }, 60_000);
    return () => clearInterval(id);
  }, [loadTenant, navigate]);

  useEffect(() => {
    const id = setInterval(() => {
      setSlide((s) => (s + 1) % SLIDES.length);
    }, 6000);
    return () => clearInterval(id);
  }, []);

  const summary = useMemo(() => {
    const name = tenant?.name || sessionStorage.getItem("onboarding_tenant_name") || "—";
    const contactEmail = tenant?.contact?.email || "—";
    const phone = tenant?.contact?.phone || "—";
    return { name, contactEmail, phone };
  }, [tenant]);

  const handleLogout = () => {
    signOut();
    navigate("/login", { replace: true });
  };

  if (loading) {
    return (
      <div className="pap-shell pap-loading">
        <p>Cargando tu solicitud…</p>
      </div>
    );
  }

  return (
    <div className="pap-shell">
      <header className="pap-header">
        <Link to="/login" className="pap-logo" aria-label="TagNize">
          <img
            src={`${process.env.PUBLIC_URL}/logo_header.png`}
            alt="TagNize"
            className="pap-logo-img"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <span className="pap-logo-fallback">TagNize</span>
        </Link>
        <button type="button" className="pap-logout" onClick={handleLogout}>
          <LogoutOutlinedIcon sx={{ fontSize: 18 }} />
          Cerrar sesión
        </button>
      </header>

      <main className="pap-grid">
        <section className="pap-copy">
          <span className="pap-badge">Solicitud enviada</span>
          <h1 className="pap-title">Tu solicitud está pendiente de aprobación</h1>
          <p className="pap-desc">
            Revisamos tu registro en un plazo de 24–48 h hábiles. Te avisaremos por email
            cuando tu workspace esté activo.
          </p>

          <div className="pap-summary">
            <div className="pap-summary-row">
              <span>Workspace</span>
              <strong>{summary.name}</strong>
            </div>
            <div className="pap-summary-row">
              <span>Email contacto</span>
              <strong>{summary.contactEmail}</strong>
            </div>
            <div className="pap-summary-row">
              <span>Teléfono</span>
              <strong>{summary.phone}</strong>
            </div>
          </div>

          <a className="pap-btn-primary" href={buildMailto(tenant)}>
            <MailOutlineIcon sx={{ fontSize: 18 }} />
            Contactar administrador
          </a>
          <p className="pap-cta-hint">
            Se abrirá tu cliente de correo con el mensaje listo para enviar.
          </p>
        </section>

        <ProductPreviewShowcase slide={slide} onSlideChange={setSlide} />
      </main>
    </div>
  );
}

export async function finalizeOnboardingSubmission(navigate) {
  const result = await submitTenantForApproval();
  const tenant = result.tenant;
  const claims = await getIdTokenClaims().catch(() => ({}));
  const userKey = userKeyFromClaims(claims);
  markOnboardingSubmitted(userKey, tenant.tenant_id, tenant.name);
  sessionStorage.setItem("onboarding_tenant_id", tenant.tenant_id);
  sessionStorage.setItem("onboarding_tenant_name", tenant.name);

  let userEmail;
  try {
    userEmail = JSON.parse(sessionStorage.getItem("onboarding_profile") || "{}").email;
  } catch {
    userEmail = undefined;
  }
  trackEvent("onboarding_completed", {
    event_category: "onboarding",
    event_label: "onboarding_finished",
    tenant_id: tenant.tenant_id,
    user_email: userEmail,
  });

  await forceRefreshSession().catch(() => {});
  navigate("/solicitud-pendiente", { replace: true });
}
